import React, { useMemo, useState } from 'react';
import type { SavedLocation } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { useSavedLocationDrawerStore } from '@hero/hooks-shared';
import { useSavedLocationApi } from '@hero/api-hooks';
import { useFeature } from '@hero/web-context';
import { PencilIcon } from '@hero/web-icons';
import { StackedDrawer } from '../../../app/Drawer/StackedDrawer';
import type { ActionButtonGroup } from '../../../app/ActionButton/ActionButton';
import { SavedLocationSummaryTab } from './SavedLocationSummaryTab';
import { SavedLocationOrdersTab } from './SavedLocationOrdersTab';
import { DrawerHeader } from '../../../app/Drawer/DrawerHeader';
import { DrawerTabs } from '../../../app/Drawer/DrawerTabs';
import { DrawerContent } from '../../../app/Drawer/DrawerContent';
import { DrawerFooter } from '../../../app/Drawer/DrawerFooter';
import { LocationEditModal } from '../Modal/SavedLocationEditModal';

export interface SavedLocationDrawerProps {
  displayForUser?: 'customer' | 'carrier';
  syncUrl?: boolean | string;
}

interface SavedLocationDrawerContentProps {
  savedLocation: SavedLocation;
  displayForUser: 'customer' | 'carrier';
  onClose: () => void;
}

const SavedLocationDrawerContent: React.FC<SavedLocationDrawerContentProps> = ({
  savedLocation,
  displayForUser,
  onClose,
}) => {
  const { t } = useTranslation(['m-location']);
  const { isEnabled: canEditSavedLocation } = useFeature(
    'saved_location_module',
    'sub_edit_saved_location',
  );
  const [activeTab, setActiveTab] = useState('summary');
  const [editModalOpen, setEditModalOpen] = useState(false);

  const tabs = [
    { id: 'summary', label: t('drawer.tabs.summary') },
    { id: 'orders', label: t('drawer.tabs.orders') },
  ];

  const actions: ActionButtonGroup[] = useMemo(
    () => [
      {
        id: 'edit',
        label: t('drawer.edit'),
        description: t('drawer.edit_description'),
        icon: <PencilIcon className="w-4 h-4" />,
        onClick: () => setEditModalOpen(true),
        hide: !canEditSavedLocation,
      },
    ],
    [t, canEditSavedLocation],
  );

  const renderTab = () => {
    switch (activeTab) {
      case 'summary':
        return <SavedLocationSummaryTab savedLocation={savedLocation} />;
      case 'orders':
        return (
          <SavedLocationOrdersTab savedLocation={savedLocation} displayForUser={displayForUser} />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <DrawerHeader
        title={savedLocation.name || ''}
        subtitle={savedLocation.client?.name}
        onClose={onClose}
      />
      <DrawerTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      <DrawerContent>{renderTab()}</DrawerContent>
      <DrawerFooter actions={actions} buttonLabel={t('drawer.actions')} />

      <LocationEditModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onLocationSaved={() => setEditModalOpen(false)}
        mode="edit"
        savedLocationId={savedLocation._id}
      />
    </>
  );
};

export const SavedLocationDrawer: React.FC<SavedLocationDrawerProps> = ({
  displayForUser = 'customer',
  syncUrl,
}) => (
  <StackedDrawer<SavedLocation>
    useStore={useSavedLocationDrawerStore}
    useItemApi={id => useSavedLocationApi(id)}
    syncUrl={syncUrl}
    defaultParamKey="locationId"
    sheetClassName="flex flex-col p-0 bg-white [&>button[class*='absolute']]:hidden"
    title="Location Details"
    description="View saved location information"
    renderContent={(savedLocation, onClose) => (
      <SavedLocationDrawerContent
        savedLocation={savedLocation}
        displayForUser={displayForUser}
        onClose={onClose}
      />
    )}
  />
);
